import { useState, useEffect, useCallback } from 'react';
import { Row, Col, Button } from 'react-bootstrap';
import { PlusLg, CashCoin, ClockHistory, CheckCircle, HourglassSplit } from 'react-bootstrap-icons';
import axios from 'axios';
import { AdminCard } from '../Admin/shared/AdminCard';
import { PostLeadModal } from '../Marketplace/PostLeadModal';

export function ReferrerDashboard({ idToken }) {
    const [stats, setStats] = useState({
        totalLeads: 0,
        soldLeads: 0,
        pendingLeads: 0,
        totalEarnings: 0
    });
    const [recentLeads, setRecentLeads] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showPostModal, setShowPostModal] = useState(false);

    const fetchData = useCallback(async () => {
        if (!idToken) return;

        try {
            setLoading(true);
            // Fetch leads created by this referrer
            const response = await axios.get(
                `${process.env.REACT_APP_CORE_API}/lead`,
                { headers: { Authorization: idToken } }
            );
            const leads = response.data || [];

            // Calculate stats
            const sold = leads.filter(l => l.status === 'Sold').length;
            const pending = leads.filter(l => l.status === 'ForSale').length;
            const earnings = leads
                .filter(l => l.status === 'Sold')
                .reduce((sum, l) => sum + (l.price || 0), 0);

            setStats({
                totalLeads: leads.length,
                soldLeads: sold,
                pendingLeads: pending,
                totalEarnings: earnings
            });
            setRecentLeads(leads.slice(0, 5));
        } catch (error) {
            console.error('Failed to fetch dashboard data:', error);
        } finally {
            setLoading(false);
        }
    }, [idToken]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const currencyFormatter = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    });

    const getStatusBadge = (status) => {
        const styles = {
            ForSale: { bg: 'var(--qmi-warning-light)', color: 'var(--qmi-warning)' },
            Sold: { bg: 'var(--qmi-success-light)', color: 'var(--qmi-success)' },
            Closed: { bg: 'var(--qmi-gray-100)', color: 'var(--qmi-gray-500)' }
        };
        const style = styles[status] || styles.ForSale;
        return (
            <span style={{
                padding: '0.25rem 0.75rem',
                borderRadius: '1rem',
                fontSize: '0.75rem',
                fontWeight: 500,
                backgroundColor: style.bg,
                color: style.color
            }}>
                {status === 'ForSale' ? 'For Sale' : status}
            </span>
        );
    };

    if (loading) {
        return (
            <div className="admin-loading">
                <div className="admin-loading-spinner" />
            </div>
        );
    }

    return (
        <div>
            <div className="admin-page-header">
                <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
                    <div>
                        <h1 className="admin-page-title">Dashboard</h1>
                        <p className="admin-page-description">
                            Submit and track your injury leads
                        </p>
                    </div>
                    <Button className="qmi-btn-primary" onClick={() => setShowPostModal(true)}>
                        <PlusLg size={16} className="me-2" />
                        Submit New Lead
                    </Button>
                </div>
            </div>

            {/* Stats Grid */}
            <Row className="mb-4">
                <Col sm={6} lg={3} className="mb-3">
                    <AdminCard>
                        <div className="d-flex align-items-center gap-3">
                            <div style={{
                                width: 48,
                                height: 48,
                                borderRadius: '0.75rem',
                                backgroundColor: 'var(--qmi-primary-light)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                                <ClockHistory size={24} style={{ color: 'var(--qmi-primary)' }} />
                            </div>
                            <div>
                                <div style={{ fontSize: '0.875rem', color: 'var(--qmi-gray-500)' }}>Total Leads</div>
                                <div style={{ fontSize: '1.5rem', fontWeight: 600 }}>{stats.totalLeads}</div>
                            </div>
                        </div>
                    </AdminCard>
                </Col>
                <Col sm={6} lg={3} className="mb-3">
                    <AdminCard>
                        <div className="d-flex align-items-center gap-3">
                            <div style={{
                                width: 48,
                                height: 48,
                                borderRadius: '0.75rem',
                                backgroundColor: 'var(--qmi-warning-light)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                                <HourglassSplit size={24} style={{ color: 'var(--qmi-warning)' }} />
                            </div>
                            <div>
                                <div style={{ fontSize: '0.875rem', color: 'var(--qmi-gray-500)' }}>Pending</div>
                                <div style={{ fontSize: '1.5rem', fontWeight: 600 }}>{stats.pendingLeads}</div>
                            </div>
                        </div>
                    </AdminCard>
                </Col>
                <Col sm={6} lg={3} className="mb-3">
                    <AdminCard>
                        <div className="d-flex align-items-center gap-3">
                            <div style={{
                                width: 48,
                                height: 48,
                                borderRadius: '0.75rem',
                                backgroundColor: 'var(--qmi-success-light)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                                <CheckCircle size={24} style={{ color: 'var(--qmi-success)' }} />
                            </div>
                            <div>
                                <div style={{ fontSize: '0.875rem', color: 'var(--qmi-gray-500)' }}>Sold</div>
                                <div style={{ fontSize: '1.5rem', fontWeight: 600 }}>{stats.soldLeads}</div>
                            </div>
                        </div>
                    </AdminCard>
                </Col>
                <Col sm={6} lg={3} className="mb-3">
                    <AdminCard>
                        <div className="d-flex align-items-center gap-3">
                            <div style={{
                                width: 48,
                                height: 48,
                                borderRadius: '0.75rem',
                                backgroundColor: 'var(--qmi-success-light)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                                <CashCoin size={24} style={{ color: 'var(--qmi-success)' }} />
                            </div>
                            <div>
                                <div style={{ fontSize: '0.875rem', color: 'var(--qmi-gray-500)' }}>Earnings</div>
                                <div style={{ fontSize: '1.5rem', fontWeight: 600 }}>{currencyFormatter.format(stats.totalEarnings)}</div>
                            </div>
                        </div>
                    </AdminCard>
                </Col>
            </Row>

            {/* Recent Leads */}
            <AdminCard>
                <h5 className="mb-3">Recent Leads</h5>
                {recentLeads.length === 0 ? (
                    <div className="text-center py-4">
                        <p className="text-muted mb-3">You haven't submitted any leads yet.</p>
                        <Button variant="outline-primary" onClick={() => setShowPostModal(true)}>
                            Submit Your First Lead
                        </Button>
                    </div>
                ) : (
                    <div className="admin-table-wrapper">
                        <table className="admin-table">
                            <thead>
                                <tr>
                                    <th>Contact</th>
                                    <th>State</th>
                                    <th>Price</th>
                                    <th>Status</th>
                                    <th>Date</th>
                                </tr>
                            </thead>
                            <tbody>
                                {recentLeads.map(lead => (
                                    <tr key={lead.id}>
                                        <td>
                                            <strong>{lead.firstName} {lead.lastName?.charAt(0)}.</strong>
                                        </td>
                                        <td>{lead.state}</td>
                                        <td>{currencyFormatter.format(lead.price || 0)}</td>
                                        <td>{getStatusBadge(lead.status)}</td>
                                        <td>{lead.createdAt ? new Date(lead.createdAt).toLocaleDateString() : 'N/A'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </AdminCard>

            <PostLeadModal
                show={showPostModal}
                onHide={() => setShowPostModal(false)}
                idToken={idToken}
                onLeadPosted={fetchData}
            />
        </div>
    );
}
